<template>
  <div class="pt-2" style="overflow: hidden;">
    <div class="flex flex-col lg:flex-row gap-5 items-stretch w-full settings-panels-wrapper">
      <!-- Left Sidebar: Organization Settings -->
      <div class="settings-sidebar-card p-4">
        <div class="px-2 pt-1 pb-3">
          <h2 class="text-heading font-bold text-gray-900 tracking-tight">Organization settings</h2>
        </div>

        <!-- Search Box -->
        <div class="mb-3 px-1">
          <div class="settings-search-wrapper">
            <v-icon small class="search-icon">mdi-magnify</v-icon>
            <input v-model="searchQuery" type="text" placeholder="Search settings" class="settings-search-input" />
            <button v-if="searchQuery" @click="searchQuery = ''" type="button" class="clear-search-btn">
              <v-icon x-small color="#94a3b8">mdi-close-circle</v-icon>
            </button>
          </div>
        </div>

        <!-- Categorized List -->
        <div class="settings-menu-list space-y-4 overflow-y-auto pr-1" style="flex: 1 1 0%; min-height: 0;">
          <div v-for="category in filteredCategories" :key="category.name" class="category-block">
            <!-- Category Title -->
            <div class="category-title">
              {{ category.name }}
            </div>

            <!-- Items in Category -->
            <div class="space-y-1">
              <button v-for="item in category.items" :key="item.name" type="button"
                @click="handleTabSelect(item.originalIndex)" class="settings-menu-item" :class="{
                  'settings-item-active': activeTab === item.originalIndex
                }">
                <!-- Icon Container -->
                <div class="settings-icon-box" :class="{
                  'settings-icon-active': activeTab === item.originalIndex,
                  'settings-icon-inactive': activeTab !== item.originalIndex
                }">
                  <v-icon size="22" :color="activeTab === item.originalIndex ? '#ffffff' : '#475569'">
                    {{ item.icon }}
                  </v-icon>
                </div>

                <!-- Title & Description -->
                <div class="min-w-0 flex-1">
                  <div class="item-title truncate" :class="{ 'item-title-active': activeTab === item.originalIndex }">
                    {{ item.title || item.label }}
                  </div>
                  <div class="item-description truncate">
                    {{ item.description }}
                  </div>
                </div>
              </button>
            </div>
          </div>

          <div v-if="filteredCategories.length === 0" class="text-center py-8 text-gray-400 text-sm">
            <v-icon color="#94a3b8" class="mb-2">mdi-text-box-search-outline</v-icon>
            <div>No settings found</div>
          </div>
        </div>
      </div>

      <!-- Right Content Area -->
      <div class="settings-content-card flex-1 min-w-0 p-6">
        <div v-for="(tab, index) in tabs" :key="tab.name" v-show="activeTab === index">
          <div v-if="shouldShowHeader(tab.name)"
            class="flex justify-between items-center pb-3 border-b border-gray-100">
            <div>
              <h3 class="text-lg font-bold text-gray-900 mb-0.5">{{ tab.title || tab.label }}</h3>
              <p v-if="tab.description" class="text-xs text-gray-400 mb-0">{{ tab.description }}</p>
            </div>
            <div class="flex justify-end items-center gap-2 ml-auto">
              <v-btn :color="$vuetify.theme.currentTheme.primary" class="white--text normal-case rounded-lg" depressed
                @click="handleUpdateClick(tab)" :disabled="disableUpdateButton"
                v-show="shouldShowUpdateButton(tab.name)">
                Update
              </v-btn>
              <!-- <v-tooltip bottom v-if="tab.name === 'orgDetails'">
                <template v-slot:activator="{ on, attrs }"> -->
              <v-btn text v-if="tab.name === 'orgDetails'" small color="primary" v-bind="attrs" v-on="on"
                @click="openMapDialog" class="hover:bg-blue-50">
                View Map
              </v-btn>
              <!-- </template> -->
              <!-- <span>Company / Branches Locations</span> -->
              <!-- </v-tooltip> -->
            </div>
          </div>

          <component :is="tab.component" :receivedData="receivedData" :orgData="orgData" :branches="branches"
            :errors="errors" @refetchData="refetchData" v-on="getEventListeners(tab)" v-bind="getProps(tab)"
            :ref="`component-${tab.name}`" />
        </div>
      </div>
    </div>

    <AlertPopUp :showConfirmationDialog="showReloadDialog" :confirmationTitle="''"
      :confirmationMessage="'Please Reload to update your config changes'" :confirmationActionText="'Reload'"
      :confirmationActionColor="'red'" :performAction="ReloadFunc" :cancel="handleReloadDialog" :loading="loading" />

    <!-- Company / Branches Locations Map Dialog -->
    <v-dialog v-model="mapDialog" max-width="1000" scrollable>
      <v-card class="overflow-hidden">
        <div class="flex justify-between items-center px-5 py-4 border-b border-gray-100">
          <div class="flex items-center gap-2.5">
            <v-icon color="primary">mdi-map-marker-radius</v-icon>
            <div>
              <h3 class="text-heading font-semibold text-gray-900 mb-0">Company / Branches Locations</h3>
              <p class="text-caption text-gray-500 mb-0">
                {{ branches.length }} {{ branches.length === 1 ? 'branch location' : 'branch locations' }}
              </p>
            </div>
          </div>
          <v-btn icon small @click="closeMapDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-card-text class="p-4">
          <div class="w-full h-[600px] rounded-lg overflow-hidden relative border border-gray-200">
            <div ref="branchesMap" class="w-full h-full min-h-[580px]"></div>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <div class="flex justify-between items-center px-5 py-3 bg-gray-50">
          <div class="text-caption text-gray-500 flex items-center gap-1.5">
            <v-icon x-small color="grey">mdi-information-outline</v-icon>
            <span>Click any marker to view branch details and address</span>
          </div>
          <v-btn outlined small color="grey darken-2" class="normal-case rounded-lg font-medium"
            @click="closeMapDialog">
            Close
          </v-btn>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/* global google */
import { convertToTitleCase } from '@/utils/common'
import TaskCompletionSteps from './TaskCompletionSteps.vue'
import Questionnaire from './Questionnaire.vue'
import AttendanceSetting from './AttendanceSetting.vue'
import CustomSwitch from './CustomSwitch.vue'
import CustomComboBox from './CustomComboBox.vue'
// import NotificationIntegration from './NotificationIntegration/index.vue'
import PlatformIntegrations from './PlatformIntegrations/index.vue'
import ExpenseSettings from './ExpenseSettings.vue'
import ClaimSettings from './ClaimSettings.vue'
import EnabledUseCase from './EnabledUseCase.vue'
import AlertPopUp from '@/components/AlertPopUp'
// import BranchGroup from './BranchGroup.vue'
import LanguageConfig from './LanguageConfiguration.vue'
import {
  hasPermission,
  isAnyFeatureAllowed,
  isModuleVisible
} from '@/utils/common'
import permissionData from '@/utils/permissions'
import SalaryConfig from './SalaryConfig.vue'
import OrgLicence from './OrgLicence.vue'
import CompanyDetails from '@/pages/Workforce/OrganizationPage.vue'
import AdminDashboard from '../AdminDashboardCustomization.vue'
import LiveTrackingConfig from './LiveTrackingConfig.vue'

import { axiosUrl } from '@/utils/workforceApi'

export default {
  name: 'platformComponent',
  data() {
    return {
      searchQuery: '',
      organizationData: this.$storage.getUniversal('orgData'),
      activeTab: 0,
      userData: this.$store.state.wfmPermissions,
      hasConfigChanges: false,
      isDefault: false,
      notify: {
        SMS: 'SMS',
        EMAIL: 'E-Mail',
        INAPP: 'In-App Message'
      },
      customList: [],
      questionnaire: [],
      isAnyChangeInQuestions: false,
      otpVerification: this.receivedData?.customerOTPVerification,
      isValueChanged: false,
      showReloadDialog: false,
      proofType: [],
      meetingOutcomes: [],
      attendanceData: {},
      attendanceDataFields: null,
      attendanceStartDataTime: '',
      attendanceEndDataTime: '',
      updateWorkHour: null,
      attendanceTimeThreshold: null,
      enabledSubCases: [],
      orgLeaves: {},
      isAutoReschedule: this.receivedData?.isAutoReschedule,
      travelData: this.receivedData?.travelModes,
      allowanceData: this.receivedData?.orgAllowances,
      notificationSetting: [],
      selectedNotification: {},
      listOfHolidays: [],
      shifts: [],
      branches: [{ branchName: '', branchDescription: '', disabled: true }],
      mapDialog: false,
      mapInstance: null,
      branchMarkers: [],
      branchInfoWindows: [],
      docsData: {},
      geoFenceData: null,
      enableBranchMapping: null,
      branchFenceValue: null,
      allBranchCheckIn: null,
      homeGeofenceRadius: null,
      clockInWithQrCode: false,
      isCustomerDataEncrypted: false,
      clockOutWithQrCode: false,
      loading: false,
      errors: {}
    }
  },
  components: {
    TaskCompletionSteps,
    Questionnaire,
    CustomSwitch,
    CustomComboBox,
    AttendanceSetting,
    PlatformIntegrations,
    ExpenseSettings,
    ClaimSettings,
    EnabledUseCase,
    CompanyDetails,
    AlertPopUp,
    // BranchGroup,
    SalaryConfig,
    AdminDashboard,
    // NotificationIntegration,
    LanguageConfig,
    OrgLicence,
    LiveTrackingConfig
  },
  props: {
    receivedData: {
      type: Object,
      default: null
    },
    orgData: Object
  },
  watch: {
    receivedData: {
      handler(newVal) {
        if (newVal !== null) {
          this.receivedData = newVal
        }
      },
      deep: true,
      immediate: true
    },
    // activeTab: {
    //   handler(newVal) {
    //     if (newVal) {
    //       const configSetting = this.$route?.query?.config_tab
    //       if (configSetting) {
    //         this.activeTab = this.tabs.findIndex((tab) => tab.name === configSetting)
    //         if (newVal !== this.activeTab) {
    //           this.$router.replace({ query: null })
    //         }
    //       }
    //     }
    //   }
    // },
    '$route.query.subActiveTab': {
      handler(newQuery) {
        try {
          const currentSubTab = newQuery

          const tabIndex = this.tabs.findIndex((tab) => tab.name === currentSubTab)

          this.activeTab = tabIndex

          this.handleTabChange(tabIndex)
        } catch (error) {
          console.log(error)
        }
      },
      immediate: true
    }
  },
  mounted() {
    const currentSubTab = this.$route.query.subActiveTab
    const tabIndex = this.tabs.findIndex((tab) => tab.name === currentSubTab)
    if (tabIndex >= 0) {
      this.activeTab = tabIndex
    } else {
      this.activeTab = 0
      this.handleTabChange(this.activeTab)
    }
    this.isValueChanged = false
    this.fetchBranches()
    const configSetting = this.$route?.query?.config_tab
    const savedTab = localStorage.getItem('settingActiveTab')
    this.activeTab = 0
    if (savedTab) {
      this.activeTab = parseInt(savedTab)
      localStorage.removeItem('settingActiveTab')
    }
    if (configSetting) {
      this.activeTab = this.tabs.findIndex((tab) => tab.name === configSetting)
    }
  },
  computed: {
    canEdit() {
      return hasPermission(this.userData, permissionData.settingEdit)
    },
    disableUpdateButton() {
      return !(this.isValueChanged || this.hasConfigChanges)
    },
    settingLanguage() {
      const Cases = this.receivedData?.enabledUseCases
      if (Cases && Cases.includes('inventory') && Cases.length > 1) {
        return 'Task/Complaint'
      } else if (Cases && Cases.includes('inventory')) {
        return 'Complaint'
      } else {
        return 'Task'
      }
    },
    tabs() {
      const allTabs = [
        {
          name: 'orgDetails',
          label: 'Details',
          title: 'Company profile',
          description: 'Registrations and bank accounts',
          icon: 'mdi-file-document-outline',
          category: 'ORGANIZATION',
          component: 'CompanyDetails',
          visible: true
        },
        {
          name: 'enabledUseCase',
          label: 'UseCase Setup',
          title: 'Business modules',
          description: 'Choose what your teams use',
          icon: 'mdi-view-grid-plus-outline',
          category: 'ORGANIZATION',
          component: 'EnabledUseCase',
          visible: true
        },
        {
          name: 'taskCompletionSteps',
          label: 'ToDo Settings',
          title: 'Tasks',
          description: 'Defaults and reminders',
          icon: 'mdi-checkbox-marked-circle-outline',
          category: 'OPERATIONS',
          component: 'TaskCompletionSteps',
          visible: isAnyFeatureAllowed('TASK_MANAGEMENT')
        },
        {
          name: 'attendanceSetting',
          label: 'Attendance',
          title: 'Attendance rules',
          description: 'Hours, grace and check-in',
          icon: 'mdi-clock-outline',
          category: 'OPERATIONS',
          component: 'AttendanceSetting',
          visible:
            isAnyFeatureAllowed('ATTENDANCE_MANAGEMENT') && isModuleVisible('attendance_management')
        },
        {
          name: 'liveTrackingConfig',
          label: 'Location Tracking',
          title: 'Location tracking',
          description: 'Field tracking and privacy',
          icon: 'mdi-map-marker-outline',
          category: 'OPERATIONS',
          component: 'LiveTrackingConfig',
          visible: true
        },
        {
          name: 'claimSettings',
          label: 'Claim Setup',
          title: 'Claims',
          description: 'Distance-wise KM rates & flat amounts by travel mode',
          icon: 'mdi-car-speed-limiter',
          category: 'OPERATIONS',
          component: 'ClaimSettings',
          visible: true
        },
        {
          name: 'AdminDashboardCustomization',
          label: 'Dashboard Widget',
          title: 'Dashboard widgets',
          description: 'What shows on the dashboard',
          icon: 'mdi-view-dashboard-outline',
          category: 'WORKSPACE',
          component: 'AdminDashboard',
          visible: true
        },
        {
          name: 'platformIntegrations',
          label: 'Platform Integrations',
          title: 'Integrations',
          description: 'Connect your other tools',
          icon: 'mdi-power-plug-outline',
          category: 'WORKSPACE',
          component: 'PlatformIntegrations',
          visible: isAnyFeatureAllowed('ADDONS')
        },
        {
          name: 'orgLicense',
          label: 'License/Plan Info',
          title: 'Plan & billing',
          description: 'Subscription and invoices',
          icon: 'mdi-credit-card-outline',
          category: 'ACCOUNT',
          component: 'OrgLicence',
          visible: true
        }
      ]
      return allTabs.filter((tab) => tab.visible)
    },
    categories() {
      const categoryOrder = ['ORGANIZATION', 'OPERATIONS', 'WORKSPACE', 'ACCOUNT']
      const groups = {}
      categoryOrder.forEach((cat) => {
        groups[cat] = []
      })

      this.tabs.forEach((tab, index) => {
        const cat = tab.category || 'ORGANIZATION'
        if (!groups[cat]) {
          groups[cat] = []
        }
        groups[cat].push({
          ...tab,
          originalIndex: index
        })
      })

      return Object.keys(groups)
        .filter((cat) => groups[cat].length > 0)
        .map((cat) => ({
          name: cat,
          items: groups[cat]
        }))
    },
    filteredCategories() {
      if (!this.searchQuery || !this.searchQuery.trim()) {
        return this.categories
      }
      const q = this.searchQuery.toLowerCase().trim()
      return this.categories
        .map((cat) => {
          const matchingItems = cat.items.filter((item) => {
            const title = (item.title || item.label || '').toLowerCase()
            const desc = (item.description || '').toLowerCase()
            return title.includes(q) || desc.includes(q)
          })
          return {
            name: cat.name,
            items: matchingItems
          }
        })
        .filter((cat) => cat.items.length > 0)
    }
  },

  methods: {
    handleTabSelect(index) {
      this.activeTab = index
      this.handleTabChange(index)
    },
    handleTabChange(tabKey) {
      localStorage.setItem('settingActiveTab', tabKey)
      const newSubTab = this.tabs[tabKey]?.name
      if (this.$route.query.subActiveTab !== newSubTab) {
        this.$router
          .replace({
            query: {
              ...this.$route.query,
              subActiveTab: newSubTab
            }
          })
          .catch((err) => {
            if (err.name !== 'NavigationDuplicated') throw err
          })
      }
    },

    shouldShowHeader(tabName) {
      const noHeaderTabs = [
        'platformIntegrations',
        'orgLicense',
        'openQuestionnaire',
        'languageConfig',
        'orgDocuments',
        'claimSettings'
      ]
      return !noHeaderTabs.includes(tabName)
    },

    shouldShowUpdateButton(tabName) {
      const noUpdateButtonTabs = [
        'salaryConfig',
        'orgLicense',
        'notificationIntegration',
        'openQuestionnaire',
        'languageConfig',
        'orgDetails',
        'AdminDashboardCustomization',
        'liveTrackingConfig',
        'claimSettings',
        'orgDocuments'
      ]
      return this.canEdit && !noUpdateButtonTabs.includes(tabName)
    },
    handleUpdateClick(tab) {
      const componentMethodMap = {
        attendanceSetting: 'saveAttendanceSettings',
        taskCompletionSteps: 'saveTaskCompletionSteps',
        claimSettings: 'saveClaimSettings'
        // Add more mappings as needed for other components
        // 'anotherTabName': 'anotherMethodName',
      }

      // Get the method name for this tab, or default to updateConfig
      const methodName = componentMethodMap[tab.name]
      // console.log('Method Name:', methodName)

      if (methodName) {
        // Call the specific component method
        const componentRef = this.$refs[`component-${tab.name}`]
        if (componentRef && componentRef[0] && typeof componentRef[0][methodName] === 'function') {
          componentRef[0][methodName]()
          this.hasConfigChanges = false // Reset the flag after saving
        }
      } else {
        // Default to updateConfig for tabs without specific methods
        this.updateConfig()
      }
    },

    openMapDialog() {
      this.mapDialog = true
      this.$nextTick(() => {
        setTimeout(() => {
          this.initOrRefreshMap()
        }, 300)
      })
    },
    closeMapDialog() {
      this.mapDialog = false
    },
    async initOrRefreshMap() {
      if (typeof window.google === 'undefined' || !window.google.maps) {
        await this.addGoogleMapsScript()
        return
      }

      if (!this.$refs.branchesMap) {
        return
      }

      if (!this.mapInstance || (this.mapInstance.getDiv && this.mapInstance.getDiv() !== this.$refs.branchesMap)) {
        await this.initMap()
      } else {
        window.google.maps.event.trigger(this.mapInstance, 'resize')
        if (this.branchMarkers && this.branchMarkers.length > 0) {
          const bounds = new window.google.maps.LatLngBounds()
          this.branchMarkers.forEach((m) => {
            if (m && m.getPosition && m.getPosition()) bounds.extend(m.getPosition())
          })
          this.mapInstance.fitBounds(bounds)
        }
      }
    },
    async addGoogleMapsScript() {
      try {
        const scriptTag = document.querySelector('script[src*="maps.googleapis.com"]')
        if (!scriptTag) {
          const script = document.createElement('script')
          script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.VUE_APP_MAP_KEY}&libraries=places&callback=Function.prototype`
          script.defer = true
          script.async = true
          script.onload = () => {
            if (this.mapDialog) {
              this.initMap()
            }
          }
          document.head.appendChild(script)
        } else if (this.mapDialog) {
          setTimeout(() => {
            this.initMap()
          }, 300)
        }
      } catch (error) {
        console.error('Error loading Google Maps script:', error)
      }
    },
    async initMap() {
      try {
        if (!this.$refs.branchesMap) {
          return
        }

        const mapOptions = {
          center: { lat: 20.5937, lng: 78.9629 },
          zoom: 5
        }
        this.mapInstance = new window.google.maps.Map(this.$refs.branchesMap, mapOptions)

        if (!this.branches || !this.branches.length || (this.branches.length === 1 && !this.branches[0].branchName)) {
          await this.fetchBranches()
        }

        this.displayBranchMarkers()
      } catch (error) {
        console.error('An error occurred while initializing the map:', error)
      }
    },
    displayBranchMarkers() {
      if (typeof window.google === 'undefined' || !window.google.maps || !this.mapInstance) return

      this.clearBranchMarkers()

      const bounds = new window.google.maps.LatLngBounds()
      let hasValidCoordinates = false

      // 1. Add Main Organization Address Marker
      const orgAddress = this.orgData?.organization_address || this.organizationData?.organization_address
      if (orgAddress && (orgAddress.lat || orgAddress.location?.lat)) {
        const lat = Number(orgAddress.lat || orgAddress.location?.lat)
        const lng = Number(orgAddress.lng || orgAddress.location?.lng)
        if (!isNaN(lat) && !isNaN(lng)) {
          const pos = { lat, lng }
          const orgMarker = new window.google.maps.Marker({
            position: pos,
            map: this.mapInstance,
            title: this.orgData?.name || 'Head Office',
            icon: {
              url: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
            }
          })
          const infoWindow = new window.google.maps.InfoWindow({
            content: `<div class="p-2"><h4 class="font-bold text-sm text-gray-900">${this.orgData?.name || 'Head Office'}</h4><p class="text-xs text-gray-600 mt-1 mb-0">${orgAddress.address_line_1 || ''}</p></div>`
          })
          orgMarker.addListener('click', () => {
            infoWindow.open(this.mapInstance, orgMarker)
          })
          this.branchMarkers.push(orgMarker)
          this.branchInfoWindows.push(infoWindow)
          bounds.extend(pos)
          hasValidCoordinates = true
        }
      }

      // 2. Add Branch Markers
      const branchesList = this.branches || []
      branchesList.forEach((branch) => {
        const loc = branch.geoFencingData?.location || branch.location || branch.branchLocation
        const lat = Number(loc?.lat || loc?.latitude)
        const lng = Number(loc?.lng || loc?.longitude)

        if (!isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0)) {
          const position = { lat, lng }
          const marker = new window.google.maps.Marker({
            position,
            map: this.mapInstance,
            title: branch.branchName || 'Branch'
          })

          let infoContent = `<div class="p-2">`
          infoContent += `<h4 class="font-bold text-sm text-gray-900">${branch.branchName || 'Branch'}</h4>`
          if (branch.branchLocation?.addressLine1 || branch.addressLine1) {
            infoContent += `<p class="text-xs text-gray-600 mt-1 mb-0">${branch.branchLocation?.addressLine1 || branch.addressLine1}</p>`
          }
          if (branch.branchLocation?.city || branch.branchLocation?.state) {
            infoContent += `<p class="text-xs text-gray-500 mb-0">${branch.branchLocation.city || ''} ${branch.branchLocation.state || ''}</p>`
          }
          infoContent += `</div>`

          const infoWindow = new window.google.maps.InfoWindow({
            content: infoContent
          })

          marker.addListener('click', () => {
            infoWindow.open(this.mapInstance, marker)
          })

          this.branchMarkers.push(marker)
          this.branchInfoWindows.push(infoWindow)
          bounds.extend(position)
          hasValidCoordinates = true
        }
      })

      if (hasValidCoordinates && this.branchMarkers.length > 0) {
        this.mapInstance.fitBounds(bounds)
        if (this.branchMarkers.length === 1) {
          window.google.maps.event.addListenerOnce(this.mapInstance, 'bounds_changed', () => {
            if (this.mapInstance.getZoom() > 15) {
              this.mapInstance.setZoom(15)
            }
          })
        }
      }
    },
    clearBranchMarkers() {
      if (this.branchInfoWindows && this.branchInfoWindows.length) {
        this.branchInfoWindows.forEach((iw) => {
          if (iw && iw.close) iw.close()
        })
      }
      if (this.branchMarkers && this.branchMarkers.length) {
        this.branchMarkers.forEach((m) => {
          if (m && m.setMap) m.setMap(null)
        })
      }
      this.branchMarkers = []
      this.branchInfoWindows = []
    },

    getDisabledState(tab) {
      // Special cases for different tabs
      // if (tab.name === 'openQuestionnaire') {
      //   return !this.hasConfigChanges
      // }

      // Default case
      return this.disableUpdateButton
    },

    refetchData() {
      this.$emit('refetchData')
    },
    async updateConfig() {
      try {
        let taskCompletionSteps = [],
          type = 'DEFAULT',
          taskCompletionStepsConfig = []
        if (!this.isDefault) {
          taskCompletionSteps = this.customList
            .filter((item) => item.value.checked)
            .map((item) => item.key)
          taskCompletionStepsConfig = this.customList
            .filter((item) => item.value.checked)
            .map((item) => ({ step: item.key, canSkip: item.value.canSkip }))
          type = 'CUSTOM'
        } else {
          this.customList.forEach((item) => {
            item.value.checked = false
          })
        }

        const requestBody = {
          customerOTPVerification: this.otpVerification,
          isAutoReschedule: this.isAutoReschedule,
          encryptCustomerDetails: this.isCustomerDataEncrypted
        }
        if (taskCompletionSteps.length) {
          requestBody.taskCompletionStepsConfig = taskCompletionStepsConfig
          requestBody.taskCompletionSteps = taskCompletionSteps
          requestBody.type = type
        }
        if (this.questionnaire.length || this.isAnyChangeInQuestions) {
          requestBody.questionnaire = this.questionnaire
        }
        if (this.meetingOutcomes.length) {
          requestBody.allowedMeetingOutcomesTypes = this.meetingOutcomes
        }
        if (this.proofType.length) {
          requestBody.allowedProofTypes = this.proofType
        }
        if (this.attendanceData) {
          requestBody.shiftStartTime = this.attendanceData?.starttime
          requestBody.shiftEndTime = this.attendanceData?.endtime
          requestBody.workingDays = this.attendanceData?.days
          requestBody.fullDayThreshold = this.attendanceData?.fullDayThreshold
          requestBody.workingHours = this.attendanceData?.workingHours
          requestBody.autoAttendanceMark = this.attendanceData?.autoAttendanceMark
          requestBody.autoCheckout = this.attendanceData?.autoCheckout
          requestBody.strictCheckout = this.attendanceData?.strictCheckout
          requestBody.isCheckedInSelfie = this.attendanceData?.isCheckedInSelfie
          requestBody.clockInWithQrCode = this.attendanceData?.clockInWithQrCode
          requestBody.clockOutWithQrCode = this.attendanceData?.clockOutWithQrCode
          requestBody.isCheckedOutSelfie = this.attendanceData?.isCheckedOutSelfie
          requestBody.geoFencingEnabled = this.attendanceData?.geoFencingEnabled
          requestBody.allowSystemCalculation = this.attendanceData?.allowSystemCalculation
          requestBody.allowCheckInAllTime = this.attendanceData?.allowCheckInAllTime
          requestBody.homeGeofenceEnabled = this.attendanceData?.homeGeofenceEnabled
        }
        if (this.attendanceDataFields) {
          requestBody.workingDays = this.attendanceDataFields
        }
        if (this.attendanceStartDataTime) {
          requestBody.shiftStartTime = this.attendanceStartDataTime
        }
        if (this.attendanceEndDataTime) {
          requestBody.shiftEndTime = this.attendanceEndDataTime
        }
        if (this.updateWorkHour) {
          requestBody.workingHours = this.updateWorkHour
        }
        if (this.attendanceTimeThreshold) {
          requestBody.fullDayThreshold = this.attendanceTimeThreshold
        }
        if (this.attendanceTimeHalfThreshold) {
          requestBody.halfDayThreshold = this.attendanceTimeHalfThreshold
        }
        if (this.attendanceTimeOverThreshold) {
          requestBody.overTimeThreshold = this.attendanceTimeOverThreshold
        }
        if (this.shifts.length) {
          requestBody.orgShifts = this.shifts
        } else {
          requestBody.orgShifts = this.$props.receivedData?.orgShifts
        }
        if (this.waBussinessId) {
          requestBody.applications = {
            ...this.receivedData.applications,
            whatsapp: {
              ...this.receivedData.applications.whatsapp,
              waBussinessId: this.waBussinessId
            }
          }
        }
        if (this.travelData?.length || this.allowanceData) {
          requestBody.travelModes = this.travelData
          requestBody.orgAllowances = this.allowanceData
        }

        if (this.enabledSubCases?.length) {
          requestBody.enabledSubCases = this.enabledSubCases
        }

        if (this.orgLeaves) {
          requestBody.commonLeaves = this.orgLeaves?.commonLeaves
        }
        if (this.orgLeaves?.commonLeavePolicy) {
          requestBody.commonLeavePolicy = this.orgLeaves?.commonLeavePolicy
        }
        if (this.orgLeaves) {
          requestBody.listOfHolidays = this.orgLeaves?.listOfHolidays
        }

        if (this.notificationSetting.length) {
          requestBody.notificationConfig = this.notificationSetting
        }

        if (this.selectedNotification) {
          requestBody.isSmsNotificationEnable = this.selectedNotification.SMS
          requestBody.isEmailNotificationEnable = this.selectedNotification.EMAIL
          requestBody.isInAppNotificationEnable = this.selectedNotification.INAPP
        }

        if (this.docsData.allowedProofTypes) {
          requestBody.allowedProofTypes = this.docsData.allowedProofTypes
        }
        if (this.docsData.predefinedComplaintTitles) {
          requestBody.predefinedComplaintTitles = this.docsData.predefinedComplaintTitles
        }
        if (this.docsData.taskEstimationThreshold) {
          requestBody.taskEstimationThreshold = this.docsData.taskEstimationThreshold
        }

        if (this.docsData.allowedMeetingOutcomesTypes) {
          requestBody.allowedMeetingOutcomesTypes = this.docsData.allowedMeetingOutcomesTypes
        }

        if (this.docsData) {
          requestBody.customerOTPVerification = this.docsData?.customerOTPVerification
          requestBody.isCollectionOtpRequired = this.docsData?.isCollectionOtpRequired
          requestBody.isAutoReschedule = this.docsData?.isAutoReschedule
          requestBody.isTaskRecurrenceAllowedOrg = this.docsData?.isTaskRecurrenceAllowedOrg
          requestBody.isTaskDirectCompletionAllowedOrg =
            this.docsData?.isTaskDirectCompletionAllowedOrg
          requestBody.isTaskSingleDayCompletionAllowed =
            this.docsData?.isTaskSingleDayCompletionAllowed
          requestBody.isTaskAutoAssignedAllowedOrg = this.docsData?.isTaskAutoAssignedAllowedOrg
          requestBody.allowTaskAutoEstimation = this.docsData?.allowTaskAutoEstimation
          requestBody.allowTaskAcknowledgement = this.docsData?.allowTaskAcknowledgement
          requestBody.isTrackEmployeeActions = this.docsData?.isTrackEmployeeActions
        }

        if (this.geoFenceData) {
          requestBody.geoFencingData = this.geoFenceData
        }
        if (this.enableBranchMapping) {
          requestBody.enableBranchMapping = this.enableBranchMapping.value
        }
        if (this.branchFenceValue) {
          requestBody.enableBranchGeofencing = this.branchFenceValue.value
        }
        if (this.allBranchCheckIn) {
          requestBody.allowCheckInFromAllDefinedLocation = this.allBranchCheckIn.value
        }
        if (this.homeGeofenceRadius) {
          requestBody.homeGeofenceRadius = this.homeGeofenceRadius
        }
        if (this.attendanceData?.geoFencingEnabled == false) {
          requestBody.enableBranchGeofencing = false
          requestBody.allowCheckInFromAllDefinedLocation = false
          requestBody.homeGeofenceEnabled = false
          requestBody.homeGeofenceRadius = null
        }

        const response = await this.$axios.post('/org/config', requestBody)
        this.isAnyChangeInQuestions = false
        this.showReloadDialog = true
        const successMessage = response?.data?.message || 'Org config updated successfully'
        this.$toast.success(successMessage)
        localStorage.setItem('settingActiveTab', this.activeTab)
      } catch (error) {
        if (error.response && error.response.status === 500 && error.response.data.message) {
          const errorMessages = error.response.data.message

          errorMessages.forEach((errorMessage) => {
            const questionError = errorMessage.error
            const questionBody = errorMessage.questionBody
            const question = questionBody ? questionBody.question : ''
            if (questionError) {
              const errorMessageText = `${questionError}`

              this.$toast.error(errorMessageText)

              const questionIndex = this.questionnaire.findIndex((q) => q.question === question)

              if (questionIndex !== -1) {
                this.$set(this.errors, question, errorMessageText)
              }
            }
            console.log(this.errors)
          })
        } else {
          console.error(error.message)
        }
      }
    },
    async fetchBranches() {
      try {
        const response = await this.$axios.get(axiosUrl.branches.getBranches)
        this.branches = response.data.branches.map((branch) => ({
          ...branch,
          disabled: true
        }))
      } catch (error) {
        console.error('Error fetching branches:', error)
      }
    },
    changeTitleCase(str) {
      return convertToTitleCase(str)
    },
    handleOrderChange(value) {
      this.hasConfigChanges = value
    },
    handleTaskSteps(val) {
      this.customList = val.customList
      this.isDefault = val.isDefault
    },
    handleSwitch(val) {
      if (val.title === 'SEND_OTP') {
        this.otpVerification = val.value
      }
      if (val.title === 'AUTO_RESCHEDULE') {
        this.isAutoReschedule = val.value
      }
      this.isValueChanged = true
    },
    handleComboBox(val) {
      if (val.title === 'DOCUMENT_PROOF_TYPE') {
        this.proofType = val.value
      }
      if (val.title === 'MEETING_OUTCOMES_TYPE') {
        this.meetingOutcomes = val.value
      }
      this.isValueChanged = true
    },
    handleReloadDialog() {
      this.showReloadDialog = false
    },
    ReloadFunc() {
      this.loading = true
      window.location.reload()
    },
    getProps(tab) {
      return tab.props || {}
    },
    getEventListeners(tab) {
      if (tab) {
        return {
          customList: this.handleTaskSteps,
          questions: (val) => {
            this.isValueChanged = true
            this.questionnaire = val
            this.isAnyChangeInQuestions = true
          },
          switchValue: this.handleSwitch,
          comboBoxValue: this.handleComboBox,
          attendanceData: (val) => {
            this.isValueChanged = true
            this.attendanceData = val
          },
          attendanceDataFields: (val) => {
            this.isValueChanged = true
            this.attendanceDataFields = val
          },
          attendanceDataTime: (val) => {
            this.isValueChanged = true
            this.attendanceStartDataTime = val
          },
          attendanceDataTime2: (val) => {
            this.isValueChanged = true
            this.attendanceEndDataTime = val
          },
          updateWorkHour: (val) => {
            this.isValueChanged = true
            this.updateWorkHour = val
          },
          attendanceTimeThreshold: (val) => {
            this.isValueChanged = true
            this.attendanceTimeThreshold = val
          },
          attendanceTimeHalfThreshold: (val) => {
            this.isValueChanged = true
            this.attendanceTimeHalfThreshold = val
          },
          waBussinessId: (val) => {
            this.isValueChanged = val !== this.receivedData.applications.whatsapp.waBussinessId
            this.waBussinessId = val
          },
          attendanceTimeOverThreshold: (val) => {
            this.isValueChanged = true
            this.attendanceTimeOverThreshold = val
          },
          travelData: (val) => {
            this.isValueChanged = true
            this.travelData = val
          },
          allowanceData: (val) => {
            this.isValueChanged = true
            this.allowanceData = val
          },
          subCases: (val) => {
            this.isValueChanged = true
            this.enabledSubCases = val
          },
          orgLeaves: (val) => {
            this.isValueChanged = true
            this.orgLeaves = val
          },
          notification: (val) => {
            this.isValueChanged = true
            this.notificationSetting = val
          },
          selectedNotification: (val) => {
            this.isValueChanged = true
            this.selectedNotification = val
          },
          isUpdateDisable: () => {
            this.isValueChanged = false
          },
          orgShifts: (val) => {
            this.isValueChanged = true
            this.shifts = val
          },
          branchSwitchValue: (val) => {
            this.isValueChanged = true
            this.enableBranchMapping = val
          },
          Docs: (val) => {
            this.isValueChanged = true
            this.docsData = val
          },
          geoFenceData: (val) => {
            this.isValueChanged = true
            this.geoFenceData = val
          },
          branchFencing: (val) => {
            this.isValueChanged = true
            this.branchFenceValue = val
          },
          allBranchCheckIn: (val) => {
            this.isValueChanged = true
            this.allBranchCheckIn = val
          },
          updateHomeFenceRadius: (val) => {
            this.isValueChanged = true
            this.homeGeofenceRadius = val
          },
          customerEncrypt: (val) => {
            this.isValueChanged = true
            this.isCustomerDataEncrypted = val
          },
          isStepOrderChange: (val) => {
            this.isValueChanged = val
          },
          questionnaireDynamicForm: () => {
            this.isValueChanged = true
          },
          isDyanmicQuestionsChange: this.handleOrderChange
        }
      }
      return {}
    }
  }
}
</script>

<style scoped>
.settings-panels-wrapper {
  height: calc(100vh - 160px);
  overflow: hidden;
}

.settings-sidebar-card {
  width: 100%;
  background-color: #ffffff;
  border-radius: 20px;
  border: 1px solid #eef2f6;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

@media (min-width: 1024px) {
  .settings-sidebar-card {
    width: 360px;
    min-width: 350px;
    max-width: 380px;
    flex-shrink: 0;
  }
}

.settings-search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  pointer-events: none;
}

.clear-search-btn {
  position: absolute;
  right: 10px;
  cursor: pointer;
  border: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.settings-search-input {
  width: 100%;
  height: 42px;
  padding: 0 34px 0 36px;
  font-size: 14px;
  background-color: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  color: #1e293b;
  outline: none;
  transition: all 0.15s ease-in-out;
  box-sizing: border-box;
}

.settings-search-input:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.settings-search-input::placeholder {
  color: #94a3b8;
}

.category-title {
  font-size: var(--font-caption);
  font-weight: 700;
  letter-spacing: 0.05em;
  color: #94a3b8;
  text-transform: uppercase;
  padding-left: 8px;
  margin-bottom: 6px;
}

.settings-menu-item {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  padding: 8px 10px;
  border-radius: 14px;
  cursor: pointer;
  border: none;
  outline: none;
  background-color: transparent;
  text-align: left;
  transition: all 0.15s ease-in-out;
  user-select: none;
  box-sizing: border-box;
}

.settings-menu-item:hover:not(.settings-item-active) {
  background-color: #f8fafc;
}

.settings-item-active {
  background-color: #edf5ff !important;
  border-left: 3.5px solid #1d4ed8 !important;
}

.settings-icon-box {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-right: 12px;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.settings-icon-active {
  background-color: #1d4ed8 !important;
}

.settings-icon-inactive {
  background-color: #f1f5f9;
}

.item-title {
  font-size: var(--font-subheading-sm);
  font-weight: 600;
  color: #1e293b;
  line-height: 1.25;
}

.item-title-active {
  font-weight: 700;
  color: #0f172a;
}

.item-description {
  font-size: var(--font-caption);
  color: #64748b;
  margin-top: 2px;
  line-height: 1.25;
}

.settings-content-card {
  background-color: #ffffff;
  border-radius: 20px;
  border: 1px solid #eef2f6;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  overflow-y: overlay;
  overflow-x: hidden;
}

.hide-scrollbar::-webkit-scrollbar {
  display: none;
}

.hide-scrollbar {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

/* Dark mode */
.dark .settings-sidebar-card,
.dark .settings-content-card {
  background-color: #1f1f1f !important;
  border-color: #333333 !important;
}

.dark .settings-search-input {
  background-color: #2a2a2a !important;
  border-color: #404040 !important;
  color: #ffffff !important;
}

.dark .settings-item-active {
  background-color: rgba(37, 99, 235, 0.18) !important;
  border-left-color: #60a5fa !important;
}

.dark .settings-icon-inactive {
  background-color: #2a2a2a !important;
}

.dark .item-title {
  color: #e2e8f0;
}

.dark .item-title-active {
  color: #ffffff;
}

.dark .item-description {
  color: #94a3b8;
}

.dark .settings-menu-item:hover:not(.settings-item-active) {
  background-color: #262626 !important;
}
</style>
